import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import type {
  IApiGetOrganizationResponseBody,
  IApiGetOrganizationsResponseBody,
  IApiUpdateOrganizationRequestBody,
} from '../OrganizationsApi.types';
import { useApi } from '~/lib/api/useApi';
import { organizationQueryKeyFactory } from './useOrganizationQuery';
import { organizationsQueryKeyFactory } from './useOrganizationsQuery';

export type IUseUpdateOrganizationMutationValues = {
  organizationId: string;
  body: IApiUpdateOrganizationRequestBody;
};

export const useUpdateOrganizationMutation = (): UseMutationResult<
  IApiGetOrganizationResponseBody,
  unknown,
  IUseUpdateOrganizationMutationValues
> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) =>
      api.organizations.update(values.organizationId, values.body),
    onSuccess: (updatedOrganization) => {
      queryClient.setQueryData(
        organizationQueryKeyFactory({
          organizationId: updatedOrganization._id,
        })!,
        updatedOrganization,
      );
      queryClient.setQueriesData<IApiGetOrganizationsResponseBody>(
        { queryKey: organizationsQueryKeyFactory() },
        (previous): IApiGetOrganizationsResponseBody =>
          previous
            ? {
                ...previous,
                items: previous.items.map((organization) =>
                  organization._id === updatedOrganization._id
                    ? updatedOrganization
                    : organization,
                ),
              }
            : {
                items: [updatedOrganization],
              },
      );
    },
  });
};
